import { AlertTriangle } from "lucide-react";
import { useServiceAvailability } from "~/lib/service-availability";
import { useAuthStore } from "~/store/authentication/useAuthStore";

export const SERVICE_UNAVAILABLE_TITLE = "Service temporarily unavailable — retrying";
export const OFFLINE_TITLE = "You're offline — retrying";
export const SERVICE_UNAVAILABLE_BODY =
  "You're still signed in. What's on screen may be out of date, and changes can't be saved until we reconnect.";

/**
 * INV-RC-17 — the one notice for "Palate is temporarily unavailable" (a 503
 * SESSION_STORE_UNAVAILABLE, a gateway error, or no connection), shown instead
 * of signing the user out (~/lib/service-availability). It goes away by
 * itself: the recovery probe asks again on the server's Retry-After, and the
 * first authenticated answer ends the outage.
 *
 * Non-blocking by construction. It floats over the top of the page rather
 * than displacing the layout, and it takes no pointer events at all: nothing
 * under it is ever unclickable, and — the reason it has no "Retry now" button —
 * a click on it can never count as a click OUTSIDE an open dialog, which Radix
 * treats as "dismiss" and which would throw away the form the user is trying
 * to save. Styled as the DESIGN_SYSTEM.md § Banner `warning` variant, with the
 * body on amber-700 (4.87:1 on amber-50) rather than the spec's amber-600,
 * which fails WCAG AA (§ Status Colors, #736).
 *
 * Mounted once in root.tsx. Sits just below the support-mode banner when that
 * one is showing (`--impersonation-offset`).
 */
export function ServiceUnavailableBanner() {
  const signedIn = useAuthStore((state) => Boolean(state.token));
  const { unavailable, outage } = useServiceAvailability();

  if (!signedIn || !unavailable) return null;

  const title = outage?.kind === "offline" ? OFFLINE_TITLE : SERVICE_UNAVAILABLE_TITLE;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-[calc(var(--impersonation-offset,0px)+0.75rem)] z-[9998] flex justify-center px-4">
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="flex w-full max-w-lg items-start gap-3 rounded-[12px] border border-solid border-amber-200 bg-amber-50 p-4 shadow-md"
      >
        <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-100 p-1.5">
          <AlertTriangle className="size-4 text-amber-600" aria-hidden="true" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-bold leading-5 text-amber-800">{title}</p>
          <p className="text-xs leading-4 text-amber-700">{SERVICE_UNAVAILABLE_BODY}</p>
        </div>
      </div>
    </div>
  );
}
